import { AngularFireAuth } from "@angular/fire/compat/auth";
import { AngularFirestore, AngularFirestoreCollection } from "@angular/fire/compat/firestore";
import { distinctUntilChanged, map, switchMap, Observable, of, Subscription, BehaviorSubject } from "rxjs";
import { Injectable } from "@angular/core";
import firestore from 'firebase/compat/app';
import { isDeepEqualIgnoringFields } from "src/app/utils/methods/deep-equal";

/* Models */
import { Collection } from "src/app/v2/models/collection-reference.model";
import { Company } from "src/app/models/storage/company.model";
import { User } from "src/app/models/storage/user.model";

/* Services */
import { AlertService } from "src/app/v2/services/alert.service";
import { FirestoreService } from "src/app/v2/services/firestore.service";

/* Constants */
import { JOSH_USER_ID } from "src/environments/constants";

// Josh has several accounts; pin his UID so reporting access holds even if he signs in with one lacking role flags.
export function userCanViewReporting(user: User | null): boolean {
    return !!user && (
        user.levelType === 'Partner' ||
        user.isDeveloper === true ||
        user.isAdmin === true ||
        user.id === JOSH_USER_ID
    );
}

@Injectable({
    providedIn: 'root'
})

export class CurrentUserService {
    public get currentUser(): User | null {
        return this.userDoc;
    }

    public get currentCompany(): Company | null {
        return this.companyDoc;
    }

    public get userDocument$(): Observable<User | null> {
        return this.userDoc$.asObservable();
    }

    public get companyDocument$(): Observable<Company | null> {
        return this.companyDoc$.asObservable();
    }

    public get firestoreUser() {
        return this._firestoreUser
    }

    public get canViewReporting(): boolean {
        return userCanViewReporting(this.userDoc);
    }

    private companyDoc: Company | null = null;
    private userDoc: User | null = null;
    private companyDoc$ = new BehaviorSubject<Company | null>(null);
    private userDoc$ = new BehaviorSubject<User | null>(null);

    private companySubscription: Subscription | null = null;
    private userSubscription: Subscription | null = null;

    private _firestoreUser: firestore.User

    private initialLoad = true; // Prevents forced reloads on app startup
    public ignoreForceReload: boolean = false; // Set to true if reload is being done externally (doesn't show alert, etc)

    private versionCollection: AngularFirestoreCollection<any>;
    version: Observable<any[]>;

    constructor(private afAuth: AngularFireAuth, private firestore: AngularFirestore, private firestoreService: FirestoreService, private alertService: AlertService) {
        this.initialize();
        this.versionCollection = this.firestore.collection<any>('version');
    }

    private initialize(): void {
        if (this.userSubscription) {
            return;
        }

        this.userSubscription = this.afAuth.authState.pipe(
            switchMap(user => {
                if (!user) {
                    return of(null);
                }
                this._firestoreUser = user; // assign firestore user on sign in
                return this.firestoreService.streamDocument<User>(Collection.Users, user.uid); // Get user doc
            })
        ).subscribe(newUser => {
            if (!newUser) {
                return;
            }

            const prevUser: User | null = this.userDoc;
            const companyIDChange: boolean = prevUser?.companyId !== newUser?.companyId;

            if (prevUser && !this.initialLoad) { // Only care to force reload if not inital load, and a previous user exists
                if (!this.ignoreForceReload) { // no early return so that userDoc can still get updated if needed
                    const criticalFieldsChanged = isDeepEqualIgnoringFields(prevUser, newUser, []); // empty arr means any field changes will = true
                    if (!criticalFieldsChanged) {
                        if (companyIDChange) { // Custom message for when user's company is switched
                            this.showAlertAndForceReload("User's company has changed");
                            return;
                        }

                        this.showAlertAndForceReload("User's info has been updated."); // General Message
                        return;
                    }
                }
            }

            this.userDoc = newUser;
            this.userDoc$.next(newUser);

            if (newUser.companyId && companyIDChange) {
                this.subscribeToCompany(newUser.companyId);
            }

            this.initialLoad = false;
        });
    }

    private subscribeToCompany(companyId: string): void {
        if (this.companySubscription) {
            this.companySubscription.unsubscribe();
        }

        this.companySubscription = this.firestoreService
            .streamDocument<Company>(Collection.Companies, companyId)
            .subscribe(newCompany => {
                if (!newCompany) {
                    return;
                }

                const prevCompany: Company = this.companyDoc;
                if (prevCompany && !this.initialLoad) {
                    if (!this.ignoreForceReload) {
                        const criticalFieldsChanged = isDeepEqualIgnoringFields(prevCompany, newCompany, ['rentalNumberCurrent', 'totalProducts']);

                        if (!criticalFieldsChanged) {
                            this.showAlertAndForceReload("Company settings have been modified.");
                            return;
                        }
                    }
                }

                this.companyDoc = newCompany;
                this.companyDoc$.next(newCompany);
            });
    }

    private showAlertAndForceReload(message: string): void {
        this.alertService.fireSweetAlert(message, "A browser refresh is necessary to continue.", "info", false, true)
            .then(() => window.location.reload());
    }

    // Pulled from current user 1
    public getCurrentUserOnce(): User {
        return this.currentUser
    }

    public getCompanies() {
        return this.firestore.collection('companies', ref =>
            ref.where("active", "==", true)
                .orderBy("companyName")).snapshotChanges();
    }

    public getCompanyInfoByID(companyID): any {
        return this.firestore.collection('companies').doc(companyID).ref.get().then((res) => {
            return res.data();
        })
    }

    public updateUser(id: any, user: any) {
        return this.firestore.collection('users').doc(id).update(user);
    }

    public checkVersion() {
        let versionCol = this.firestore.collection('version').doc("releaseVersion")
        let vers = versionCol.snapshotChanges().pipe(map(changes => {
            const data = changes.payload.data();
            if (data != undefined) {
                return data;
            } else {
                return null
            }
        }))
        return vers as any
    }

    public checkVersionReleaseNote() {
        return new Promise<any[]>((resolve, reject) => {
            this.version = this.versionCollection.doc("releaseVersion").valueChanges();
            this.version
                .forEach((data) => {
                    resolve(data);
                })
                .catch((err) => reject(err));
        });
    }

    public updateUserBookingChoice(userID: string, bookingChoice: any): Promise<void> {
        return this.firestore.collection('users').doc(userID).update({ bookingChoice: bookingChoice });
    }

    public getBookingChoice(): Promise<Map<string, any>> {
        return new Promise<Map<string, any>>((resolve, reject) => {
            this.firestore.collection('users').ref.where('id', '==', this.currentUser.id).get().then(querySnapshot => {
                const bookingChoices = new Map<string, any>();
                querySnapshot.forEach((doc: any) => {
                    const data = doc.data();
                    if (data.bookingChoice) {
                        for (const key in data.bookingChoice) {
                            if (data.bookingChoice.hasOwnProperty(key)) {
                                bookingChoices.set(key, data.bookingChoice[key]);
                            }
                        }
                    }
                });
                resolve(bookingChoices);
            });
        });
    }

    public ngOnDestroy(): void {
        if (this.userSubscription) {
            this.userSubscription.unsubscribe();
        }
        if (this.companySubscription) {
            this.companySubscription.unsubscribe();
        }
    }
}
